import { supabase } from '../supabaseClient';
import { normalizeDocumentType } from './documentTypes';

// ═══════════════════════════════════════════════════════════════════════════
// Intake-3 (ADR-013) — Bridge: extracted_json → staging
//
// Determinístico. Sem LLM. Idempotente.
// Lê JSON já extraído por Intake-1/2 e cria proposed_graph_operations
// + proposed_knowledge_atoms para revisão humana no IntakeReviewQueue.
//
// Dispatcher por document_type → mappers especializados → entity resolution
// (empresa/area/oferta/imóvel) → upsert via idempotency_key.
// ═══════════════════════════════════════════════════════════════════════════

const REVIEW_STATUS_OP = 'pending';
const REVIEW_STATUS_ATOM = 'proposed';

/* ── Helpers ────────────────────────────────────────────────────────────── */

const cleanStr = (v) => (v == null ? '' : String(v).trim());
const upper = (v) => cleanStr(v).toUpperCase();

const onlyDigits = (v) => cleanStr(v).replace(/\D+/g, '');

const formatMatricula = (raw) => {
  // Aceita "120.584", "120584", "34367" → preserva input mas tenta o canônico
  const digits = onlyDigits(raw);
  if (!digits) return null;
  // imoveis.matricula no DB usa formato "XXX.XXX" (vide Eugênio 117 = "120.584").
  // Algumas matrículas extraídas vêm sem ponto ("34367") — vamos tentar ambas.
  if (digits.length >= 6) {
    return `${digits.slice(0, digits.length - 3)}.${digits.slice(-3)}`;
  }
  return cleanStr(raw);
};

const matriculaCandidates = (raw) => {
  if (!raw) return [];
  const out = new Set();
  const digits = onlyDigits(raw);
  if (digits) out.add(digits);
  const dotted = formatMatricula(raw);
  if (dotted) out.add(dotted);
  out.add(cleanStr(raw));
  return Array.from(out).filter(Boolean);
};

const extractAreaCodigoPattern = (raw) => {
  // areas.nome convencionado começa com "XXX.XXX"; o JSON traz coisas
  // como "083.103.0313-0" ou "Matrículas 34.367, 34.368, 34.369 e 63.476".
  // Pegamos o primeiro casamento XXX.XXX.
  const s = cleanStr(raw);
  if (!s) return null;
  const m = s.match(/(\d{3}\.\d{3})/);
  return m ? m[1] : null;
};

const makeIdempotencyKey = (attachmentId, operation, targetTable, detail) => {
  // attachment_id pode ser null em runs antigas — usa intake_run_id como fallback
  const parts = [attachmentId || 'no-attach', operation, targetTable];
  if (detail) parts.push(String(detail).slice(0, 80));
  return parts.join(':');
};

/* ── Entity resolution ──────────────────────────────────────────────────── */

// Tokens insignificantes que devem ser ignorados ao escolher o token de busca.
const COMPANY_STOPWORDS = new Set([
  'LTDA', 'LTDA.', 'S.A.', 'S.A', 'SA', 'EIRELI', 'ME', 'EPP',
  'DE', 'DA', 'DO', 'DAS', 'DOS', 'E', 'EM', 'A', 'O',
  'EMPREENDIMENTOS', 'EMPREENDIMENTO', 'PARTICIPACOES', 'PARTICIPAÇÕES',
  'IMOBILIARIA', 'IMOBILIÁRIA', 'IMOBILIARIOS', 'IMOBILIÁRIOS',
]);

const pickSearchToken = (nameUpper) => {
  const tokens = nameUpper.split(/\s+/).filter(Boolean);
  // Primeiro token "significativo" (>3 chars, não stopword).
  const significant = tokens.find((t) => t.length > 3 && !COMPANY_STOPWORDS.has(t));
  return significant || tokens[0] || nameUpper.slice(0, 10);
};

// TB-02.2 — extrai todos os tokens significativos (>3, não stopword) do nome
// extraído. Usado para cobertura bidirecional (cada token vira um ILIKE).
const pickSignificantTokens = (nameUpper) => {
  const tokens = nameUpper.split(/\s+/).filter(Boolean);
  const significant = tokens.filter((t) => t.length > 3 && !COMPANY_STOPWORDS.has(t));
  if (significant.length > 0) return significant;
  // Fallback: token de pickSearchToken se filtro removeu tudo
  const fallback = pickSearchToken(nameUpper);
  return fallback && fallback.length >= 2 ? [fallback] : [];
};

async function resolveEmpresa(nomeExtraido, cnpjExtraido) {
  const empty = {
    id: null, name: null, score: 0, strategy: 'none',
    weak_match: false, needs_context_resolution: true, candidates: [],
  };

  // 1. Match por CNPJ (canal mais confiável)
  const cnpjDigits = onlyDigits(cnpjExtraido);
  if (cnpjDigits.length === 14) {
    const cnpjFmt = `${cnpjDigits.slice(0, 2)}.${cnpjDigits.slice(2, 5)}.${cnpjDigits.slice(5, 8)}/${cnpjDigits.slice(8, 12)}-${cnpjDigits.slice(12)}`;
    const { data } = await supabase
      .from('empresas')
      .select('id, company_name, cnpj')
      .or(`cnpj.eq.${cnpjFmt},cnpj.eq.${cnpjDigits}`)
      .limit(1);
    if (data && data.length > 0) {
      return {
        id: data[0].id, name: data[0].company_name, score: 1.0, strategy: 'cnpj',
        weak_match: false, needs_context_resolution: false,
        candidates: [{ id: data[0].id, label: data[0].company_name, score: 1.0, matched_field: 'cnpj', weak_match: false }],
      };
    }
  }

  // 2. Match por nome — multi-token bidirecional (TB-02.2)
  // Para cada token significativo, faz ILIKE %TOKEN% e une os candidatos.
  // Score em JS combina: igualdade exata, contains bidirecional e cobertura
  // de tokens. Threshold de FK desce para 0.60 (com flag weak_match).
  const nameUpper = upper(nomeExtraido);
  if (!nameUpper) return empty;

  const tokens = pickSignificantTokens(nameUpper);
  if (tokens.length === 0) return empty;

  const candidatesMap = new Map();
  for (const tok of tokens) {
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await supabase
      .from('empresas')
      .select('id, company_name')
      .ilike('company_name', `%${tok}%`)
      .limit(20);
    if (error || !data) continue;
    for (const row of data) {
      if (!candidatesMap.has(row.id)) candidatesMap.set(row.id, row);
    }
  }

  if (candidatesMap.size === 0) return empty;

  // Score por candidato:
  //   1.00 = nome exato
  //   0.90 = contains bidirecional ("CYRELA BRAZIL REALTY" contém "CYRELA")
  //   0.85 = todos os tokens significativos do nome extraído aparecem no DB
  //   0.70 = ≥ 50% dos tokens cobertos
  //   0.60 = ao menos um token presente (weak match)
  //   0.50 = sem cobertura de token (DB matched só por substring de outro token)
  const scored = [];
  for (const e of candidatesMap.values()) {
    const cn = upper(e.company_name);
    const cnTokens = cn.split(/\s+/).filter(Boolean);
    let score = 0.50;
    if (cn === nameUpper) {
      score = 1.0;
    } else if (cn.includes(nameUpper) || nameUpper.includes(cn)) {
      score = 0.90;
    } else {
      const matched = tokens.filter((t) =>
        cn.includes(t) || cnTokens.some((cnt) => cnt.includes(t) || t.includes(cnt))
      );
      const coverage = matched.length / tokens.length;
      if (coverage >= 1) score = 0.85;
      else if (coverage >= 0.5) score = 0.70;
      else if (matched.length > 0) score = 0.60;
    }
    scored.push({
      id: e.id,
      label: e.company_name,
      score,
      matched_field: 'company_name',
      weak_match: score >= 0.60 && score < 0.80,
    });
  }
  scored.sort((a, b) => b.score - a.score);

  const best = scored[0];
  const strategy = best.score >= 0.95 ? 'exact'
    : best.score >= 0.80 ? 'fuzzy'
      : 'partial';

  return {
    id: best.id,
    name: best.label,
    score: best.score,
    strategy,
    weak_match: best.score >= 0.60 && best.score < 0.80,
    needs_context_resolution: best.score < 0.60,
    candidates: scored.slice(0, 5),
  };
}

// Classifica um score em níveis de match (P25). Usado para decidir
// confidence + _context_status nas operações propostas.
const matchQuality = (score) => {
  if (score >= 0.95) return 'strong';
  if (score >= 0.80) return 'probable';
  if (score >= 0.60) return 'weak';
  return 'none';
};
const contextStatusFromQuality = (quality) => {
  if (quality === 'strong' || quality === 'probable') return 'resolved';
  if (quality === 'weak') return 'needs_review';
  return 'unresolved';
};

async function resolveArea(areaCodigo) {
  const pattern = extractAreaCodigoPattern(areaCodigo);
  if (!pattern) return null;
  const { data } = await supabase
    .from('areas')
    .select('id, nome')
    .ilike('nome', `${pattern}%`)
    .limit(1);
  return data?.[0] || null;
}

async function resolveOferta(empresaId, areaId) {
  if (!empresaId || !areaId) return null;
  const { data } = await supabase
    .from('ofertas')
    .select('id, nome')
    .eq('empresa_id', empresaId)
    .eq('area_id', areaId)
    .limit(1);
  return data?.[0] || null;
}

async function resolveImovel(matricula) {
  const cands = matriculaCandidates(matricula);
  if (cands.length === 0) return null;
  const { data } = await supabase
    .from('imoveis')
    .select('id, nome, matricula')
    .in('matricula', cands)
    .limit(1);
  return data?.[0] || null;
}

async function resolveContatoByNome(nome) {
  const n = cleanStr(nome);
  if (!n) return null;
  const tokens = n.split(/\s+/);
  const first = tokens[0];
  const last = tokens.length > 1 ? tokens[tokens.length - 1] : null;
  if (!first || first.length < 2) return null;

  let query = supabase.from('contatos').select('id, first_name, last_name, cpf').ilike('first_name', `%${first}%`);
  if (last) query = query.ilike('last_name', `%${last}%`);
  const { data } = await query.limit(5);
  return data?.[0] || null;
}

/* ── Insert helpers ─────────────────────────────────────────────────────── */

async function insertOps(ops) {
  let created = 0;
  let skipped = 0;
  const errors = [];

  for (const op of ops) {
    try {
      // Se a chave de idempotência já existe, faz no-op silencioso.
      if (op.idempotency_key) {
        const { data: existing } = await supabase
          .from('proposed_graph_operations')
          .select('id')
          .eq('idempotency_key', op.idempotency_key)
          .limit(1);
        if (existing && existing.length > 0) {
          skipped += 1;
          continue;
        }
      }
      const { error } = await supabase.from('proposed_graph_operations').insert(op);
      if (error) throw error;
      created += 1;
    } catch (err) {
      if (err?.code === '23505') {
        skipped += 1;
      } else {
        console.error('[proposeFromExtraction] insert op falhou:', err?.message || err, op);
        errors.push(err?.message || String(err));
      }
    }
  }
  return { created, skipped, errors };
}

async function insertAtoms(atoms, intakeRunId) {
  let created = 0;
  let skipped = 0;
  const errors = [];

  if (atoms.length === 0) return { created, skipped, errors };

  // Idempotência por run: se já existem átomos para este run, pula o batch.
  const { data: existing } = await supabase
    .from('proposed_knowledge_atoms')
    .select('id')
    .eq('intake_run_id', intakeRunId)
    .limit(1);
  if (existing && existing.length > 0) {
    return { created: 0, skipped: atoms.length, errors };
  }

  for (const atom of atoms) {
    try {
      const { error } = await supabase.from('proposed_knowledge_atoms').insert(atom);
      if (error) throw error;
      created += 1;
    } catch (err) {
      console.error('[proposeFromExtraction] insert atom falhou:', err?.message || err);
      errors.push(err?.message || String(err));
    }
  }
  return { created, skipped, errors };
}

/* ── Auto-approve policy ────────────────────────────────────────────────────
 * Atoms vindos de documento (source_type='document', via PDF/upload) com
 * confidence ≥ 0.85 são auto-commitados em knowledge_atoms imediatamente.
 *
 * Por que essa policy:
 *  - Origem é determinística: documento físico, não inferência probabilística.
 *  - Threshold 0.85 corresponde a "match contains" no entity resolution —
 *    ainda alto o suficiente para ser confiável sem revisão.
 *  - Risk_level NÃO é gate: extrações de documento high-risk (ônus,
 *    proprietário, capital social) são fatos no papel, não decisões de IA.
 *
 * Ops (create_proposal/create_edge/link_document) NUNCA passam por aqui —
 * mexem no grafo, exigem revisão humana sempre.
 * ─────────────────────────────────────────────────────────────────────────── */
const AUTO_APPROVE_REASON = 'Document source + confidence >= 0.85';
const AUTO_APPROVE_THRESHOLD = 0.85;

async function autoApproveEligibleAtoms(intakeRunId) {
  const result = { autoApproved: 0, errors: [] };

  // Busca atoms ainda 'proposed' deste run que se qualificam para auto-approve.
  // source_type aqui se refere ao atom proposto — se vier ausente, default
  // 'document' (conforme contrato do bridge).
  const { data: candidates, error: qErr } = await supabase
    .from('proposed_knowledge_atoms')
    .select('id, atom_type, entity_type, entity_id, category, subcategory, content, confidence, knowledge_class, intake_run_id')
    .eq('intake_run_id', intakeRunId)
    .eq('review_status', 'proposed')
    .gte('confidence', AUTO_APPROVE_THRESHOLD);
  if (qErr) {
    console.error('[autoApproveEligibleAtoms] query falhou:', qErr.message);
    return { ...result, errors: [qErr.message] };
  }
  if (!candidates || candidates.length === 0) return result;

  const nowIso = new Date().toISOString();
  for (const atom of candidates) {
    try {
      // 1) INSERT em knowledge_atoms
      const { data: newAtom, error: insErr } = await supabase
        .from('knowledge_atoms')
        .insert({
          atom_type: atom.atom_type || 'fact',
          entity_type: atom.entity_type,
          entity_id: atom.entity_id || null,
          category: atom.category,
          subcategory: atom.subcategory || null,
          content: atom.content,
          confidence: atom.confidence,
          knowledge_class: atom.knowledge_class || null,
          source_type: 'document',
          source_ref: atom.intake_run_id,
          status: 'active',
          verified_by: 'auto_approve_policy',
          verified_at: nowIso,
        })
        .select('id')
        .single();
      if (insErr) throw insErr;

      // 2) UPDATE proposed_knowledge_atoms → approved
      const { error: upErr } = await supabase
        .from('proposed_knowledge_atoms')
        .update({
          review_status: 'approved',
          reviewed_by: 'auto_approve_policy',
          reviewed_at: nowIso,
          applied_atom_id: newAtom.id,
          auto_approval_reason: AUTO_APPROVE_REASON,
        })
        .eq('id', atom.id);
      if (upErr) throw upErr;

      result.autoApproved += 1;
    } catch (err) {
      console.error('[autoApproveEligibleAtoms] commit falhou:', err?.message || err);
      result.errors.push(err?.message || String(err));
    }
  }
  return result;
}

/* ── Mappers ────────────────────────────────────────────────────────────── */

// TB-02.1 — coverage check programático para propostas.
// Usado para sinalizar lacunas no extracted_json sem chamar LLM. Os campos
// "required" são os que precisamos para tomar decisão imobiliária; "recommended"
// são desejáveis mas não bloqueantes.
const PROPOSAL_REQUIRED_FIELDS = [
  ['oferta', 'valor_proposto'],
  ['oferta', 'data_proposta'],
  ['oferta', 'tipo_negocio'],
  ['oferta', 'condicoes_pagamento'],
];
const PROPOSAL_RECOMMENDED_FIELDS = [
  ['condicoes', 'due_diligence_dias'],
  ['termos_juridicos', 'vinculante'],
  ['imovel', 'matriculas'],
];

const getNestedValue = (obj, path) => {
  let cur = obj;
  for (const k of path) {
    if (cur == null) return undefined;
    cur = cur[k];
  }
  return cur;
};

const isMissing = (v) => {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string' && !v.trim()) return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
};

function checkProposalCoverage(j) {
  const missing = PROPOSAL_REQUIRED_FIELDS
    .filter(p => isMissing(getNestedValue(j, p)))
    .map(p => p.join('.'));
  const warnings = PROPOSAL_RECOMMENDED_FIELDS
    .filter(p => isMissing(getNestedValue(j, p)))
    .map(p => p.join('.'));
  return { missing, warnings, complete: missing.length === 0 };
}

async function mapProposalToOps(ir) {
  // Schema v2 envolve os dados em "fields"; v1 é plano. Aceita ambos.
  const raw = ir.extracted_json || {};
  const j = raw.fields || raw;
  const oferta = j.oferta || {};
  const contato = j.contato || {};
  const contraparte = j.contraparte || {};
  const imovelObj = j.imovel || {};
  const componentesPermuta = Array.isArray(j.componentes_permuta) ? j.componentes_permuta : [];
  const termosJuridicos = j.termos_juridicos || {};
  const condicoes = j.condicoes || {};
  const riscos = j.riscos || {};

  // imoveis_mencionados (v1) OU derivado de imovel.matriculas (v2)
  const imoveisMencionados = Array.isArray(j.imoveis_mencionados) && j.imoveis_mencionados.length > 0
    ? j.imoveis_mencionados
    : (Array.isArray(imovelObj.matriculas)
        ? imovelObj.matriculas.map(m => ({ matricula: m, area_m2: null, endereco: imovelObj.endereco || null }))
        : []);

  const ops = [];
  const atoms = [];

  const empresaResolved = await resolveEmpresa(oferta.empresa_compradora, null);
  const areaResolved = await resolveArea(oferta.area_codigo);
  const ofertaResolved = empresaResolved.id && areaResolved
    ? await resolveOferta(empresaResolved.id, areaResolved.id)
    : null;

  // ── create_proposal ────────────────────────────────────────
  // Match thresholds (TB-02.2): FK preenchida em strong/probable/weak
  // (score ≥ 0.60). Weak (0.60-0.79) carrega flag _weak_match para a UI
  // sinalizar revisão. Score < 0.60 deixa FK null e dispara
  // _needs_context_resolution para humano resolver manualmente.
  const empresaQuality = matchQuality(empresaResolved.score);
  const empresaIdParaFK =
    empresaQuality === 'strong'
    || empresaQuality === 'probable'
    || empresaQuality === 'weak'
      ? empresaResolved.id : null;
  const contextStatus = !ofertaResolved
    ? 'unresolved'
    : contextStatusFromQuality(empresaQuality);
  const tipoCanonico = oferta.tipo_negocio === 'permuta' ? 'physical_swap'
    : oferta.tipo_negocio === 'mista' ? 'mixed'
      : oferta.tipo_negocio === 'permuta_financeira' ? 'financial_swap'
        : 'purchase';
  const propostaNome = ir.file_name
    ? ir.file_name.replace(/\.[^.]+$/, '')
    : `Proposta ${oferta.empresa_compradora || ''} ${oferta.data_proposta || ''}`.trim();

  if (ofertaResolved) {
    const proposalData = {
      nome: propostaNome,
      tipo: tipoCanonico,
      status: 'presented',
      oferta_id: ofertaResolved.id,
      data_proposta: oferta.data_proposta || null,
      data_validade: oferta.validade || null,
      condicoes_pagamento: oferta.condicoes_pagamento || null,
      valor_proposto: oferta.valor_proposto || null,
      percentual_permuta: oferta.percentual_permuta || null,
      proponente_empresa_id: empresaIdParaFK,
      _context_status: contextStatus,
      _match_quality: empresaQuality,
      _weak_match: empresaQuality === 'weak',
      _needs_context_resolution: empresaResolved.needs_context_resolution || false,
    };

    // Confidence segue o match: strong/probable→0.85, weak→0.65, none→0.55.
    const proposalConfidence = empresaQuality === 'strong' ? 0.92
      : empresaQuality === 'probable' ? 0.85
        : empresaQuality === 'weak' ? 0.65 : 0.55;

    ops.push({
      intake_run_id: ir.id,
      operation: 'create_proposal',
      target_table: 'propostas',
      proposed_data: proposalData,
      match_strategy: empresaResolved.strategy === 'none' ? 'none' : empresaResolved.strategy,
      match_candidates: empresaResolved.candidates?.slice(0, 3) || null,
      confidence: proposalConfidence,
      source_document_id: ir.attachment_id || null,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'create_proposal', 'propostas', ofertaResolved.id),
      review_status: REVIEW_STATUS_OP,
    });
  } else {
    // Sem oferta resolvida: proposta vai para staging marcada como 'unresolved'.
    // Humano deve criar/escolher oferta antes de aprovar.
    ops.push({
      intake_run_id: ir.id,
      operation: 'create_proposal',
      target_table: 'propostas',
      proposed_data: {
        nome: propostaNome,
        tipo: tipoCanonico,
        status: 'presented',
        data_proposta: oferta.data_proposta || null,
        condicoes_pagamento: oferta.condicoes_pagamento || null,
        valor_proposto: oferta.valor_proposto || null,
        proponente_empresa_id: empresaIdParaFK,
        empresa_compradora_extraida: oferta.empresa_compradora || null,
        area_codigo_extraido: oferta.area_codigo || null,
        _context_status: 'unresolved',
        _match_quality: empresaQuality,
        _weak_match: empresaQuality === 'weak',
        _needs_context_resolution: !empresaIdParaFK,
        _missing_context: [
          !ofertaResolved && 'oferta',
          !empresaIdParaFK && 'empresa',
        ].filter(Boolean),
      },
      match_strategy: empresaResolved.strategy === 'none' ? 'none' : empresaResolved.strategy,
      match_candidates: empresaResolved.candidates?.slice(0, 3) || null,
      confidence: 0.55,
      source_document_id: ir.attachment_id || null,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'create_proposal', 'propostas', 'unresolved'),
      review_status: REVIEW_STATUS_OP,
    });
  }

  // ── link_document para attachment ↔ oferta ─────────────────
  if (ir.attachment_id && ofertaResolved) {
    ops.push({
      intake_run_id: ir.id,
      operation: 'link_document',
      target_table: 'document_links',
      proposed_data: {
        attachment_id: ir.attachment_id,
        entity_type: 'oferta',
        entity_id: ofertaResolved.id,
        relation_type: 'proposta',
      },
      match_strategy: 'exact',
      confidence: 0.90,
      source_document_id: ir.attachment_id,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'link_document', 'document_links', ofertaResolved.id),
      review_status: REVIEW_STATUS_OP,
    });
  }

  // ── create_edge proposta_imoveis ───────────────────────────
  for (const im of imoveisMencionados) {
    const imovelResolved = await resolveImovel(im.matricula);
    if (!imovelResolved) continue;
    ops.push({
      intake_run_id: ir.id,
      operation: 'create_edge',
      target_table: 'proposta_imoveis',
      proposed_data: {
        imovel_id: imovelResolved.id,
        valor_especifico: null,
        area_especifica_m2: im.area_m2 || null,
      },
      match_strategy: 'matricula',
      match_candidates: [{ id: imovelResolved.id, label: imovelResolved.nome, score: 0.95, matched_field: `matricula ${imovelResolved.matricula}` }],
      confidence: 0.90,
      source_document_id: ir.attachment_id || null,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'create_edge', 'proposta_imoveis', imovelResolved.id),
      review_status: REVIEW_STATUS_OP,
    });
  }

  // ── Atoms ──────────────────────────────────────────────────
  if (oferta.empresa_compradora) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'negotiation',
      subcategory: 'proposal_terms',
      content: `Proposta de ${oferta.empresa_compradora}: ${oferta.tipo_negocio || 'compra'}${oferta.valor_proposto ? `, valor R$ ${oferta.valor_proposto.toLocaleString('pt-BR')}` : ''}${oferta.percentual_permuta ? ` + ${oferta.percentual_permuta}% permuta` : ''}`,
      structured_data: {
        empresa: oferta.empresa_compradora,
        tipo_negocio: oferta.tipo_negocio,
        valor_proposto: oferta.valor_proposto,
        percentual_permuta: oferta.percentual_permuta,
      },
      entity_type: empresaResolved.id ? 'empresa' : null,
      entity_id: empresaResolved.id,
      confidence: 0.92,
      knowledge_class: 'fact',
      risk_level: 'high',
      reference_date: oferta.data_proposta || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  if (oferta.condicoes_pagamento) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'negotiation',
      subcategory: 'payment_terms',
      content: `Condições: ${oferta.condicoes_pagamento.slice(0, 240)}`,
      structured_data: { condicoes_pagamento: oferta.condicoes_pagamento },
      entity_type: empresaResolved.id ? 'empresa' : null,
      entity_id: empresaResolved.id,
      confidence: 0.90,
      knowledge_class: 'fact',
      risk_level: 'high',
      reference_date: oferta.data_proposta || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  // ── Contact (com guard contra "—" e separação de telefone institucional) ─
  const contatoNome = (contato.nome || '').trim();
  const contatoNomeValido = contatoNome && contatoNome !== '—';
  if (contatoNomeValido) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'contact',
      subcategory: 'proposal_contact',
      content: `Contato proposta: ${contatoNome}${contato.cargo ? ` (${contato.cargo})` : ''}${contato.email ? ` · ${contato.email}` : ''}${contato.telefone ? ` · ${contato.telefone}` : ''}`,
      structured_data: { ...contato },
      entity_type: empresaResolved.id ? 'empresa' : null,
      entity_id: empresaResolved.id,
      confidence: 0.85,
      knowledge_class: 'fact',
      risk_level: 'low',
      reference_date: oferta.data_proposta || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  } else if (contato.telefone) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'contact',
      subcategory: 'organization_phone',
      content: `Telefone institucional: ${contato.telefone}`,
      structured_data: { telefone: contato.telefone, email: contato.email || null },
      entity_type: empresaResolved.id ? 'empresa' : null,
      entity_id: empresaResolved.id,
      confidence: 0.80,
      knowledge_class: 'fact',
      risk_level: 'low',
      reference_date: oferta.data_proposta || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  // ── Contraparte (vendedor/destinatário) ─────────────────────
  if (contraparte.nome) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'negotiation',
      subcategory: 'counterparty',
      content: `Destinatário/vendedor: ${contraparte.nome}${contraparte.tipo ? ` (${contraparte.tipo})` : ''}`,
      structured_data: { ...contraparte },
      entity_type: null,
      entity_id: null,
      confidence: 0.95,
      knowledge_class: 'fact',
      risk_level: 'medium',
      reference_date: oferta.data_proposta || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  // ── Identificação do imóvel (schema v2) ─────────────────────
  if (imovelObj.endereco) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'geographic',
      subcategory: 'property_address',
      content: `Endereço: ${imovelObj.endereco}`,
      structured_data: { endereco: imovelObj.endereco },
      entity_type: 'imovel',
      entity_id: null,
      confidence: 0.95,
      knowledge_class: 'fact',
      risk_level: 'low',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (imovelObj.contribuinte_pmsp) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'geographic',
      subcategory: 'taxpayer_code',
      content: `Contribuinte/SQL: ${imovelObj.contribuinte_pmsp}`,
      structured_data: { contribuinte_pmsp: imovelObj.contribuinte_pmsp },
      entity_type: 'imovel',
      entity_id: null,
      confidence: 0.95,
      knowledge_class: 'fact',
      risk_level: 'low',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (Array.isArray(imovelObj.matriculas) && imovelObj.matriculas.length > 0) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'geographic',
      subcategory: 'registries',
      content: `Matrículas: ${imovelObj.matriculas.join(', ')}`,
      structured_data: { matriculas: imovelObj.matriculas },
      entity_type: 'imovel',
      entity_id: null,
      confidence: 0.95,
      knowledge_class: 'fact',
      risk_level: 'low',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (imovelObj.cartorio) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'geographic',
      subcategory: 'registry_office',
      content: `Cartório: ${imovelObj.cartorio}`,
      structured_data: { cartorio: imovelObj.cartorio },
      entity_type: 'imovel',
      entity_id: null,
      confidence: 0.95,
      knowledge_class: 'fact',
      risk_level: 'low',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (imovelObj.area_declarada_m2) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'geographic',
      subcategory: 'declared_area',
      content: `Área declarada: ${imovelObj.area_declarada_m2} m²`,
      structured_data: { area_declarada_m2: imovelObj.area_declarada_m2 },
      entity_type: 'imovel',
      entity_id: null,
      confidence: 0.90,
      knowledge_class: 'fact',
      risk_level: 'low',
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  // ── Componentes de permuta ─────────────────────────────────
  for (const c of componentesPermuta) {
    if (!c || (c.valor == null && c.area_m2 == null)) continue;
    const valorTxt = c.valor != null ? `R$ ${Number(c.valor).toLocaleString('pt-BR')}` : 'valor s/ informação';
    const areaTxt = c.area_m2 != null ? `${c.area_m2} m²` : '? m²';
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'financial',
      subcategory: 'swap_component',
      content: `Permuta ${c.uso || 'indefinido'}: ${valorTxt} por ${areaTxt}${c.descricao ? ' — ' + c.descricao : ''}`,
      structured_data: { ...c },
      entity_type: empresaResolved.id ? 'empresa' : null,
      entity_id: empresaResolved.id,
      confidence: 0.90,
      knowledge_class: 'fact',
      risk_level: 'high',
      reference_date: oferta.data_proposta || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  if (j.vagas_garagem != null && j.vagas_garagem !== 0) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'financial',
      subcategory: 'parking_spots',
      content: `Vagas de garagem: ${j.vagas_garagem}`,
      structured_data: { vagas_garagem: j.vagas_garagem },
      entity_type: null,
      entity_id: null,
      confidence: 0.92,
      knowledge_class: 'fact',
      risk_level: 'medium',
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  // ── Termos jurídicos ───────────────────────────────────────
  if (termosJuridicos.vinculante && termosJuridicos.vinculante !== 'nao_informado') {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'binding_status',
      content: `Proposta ${termosJuridicos.vinculante === 'nao_vinculante' ? 'NÃO vinculante' : 'VINCULANTE'}`,
      structured_data: { vinculante: termosJuridicos.vinculante },
      entity_type: null,
      entity_id: null,
      confidence: 0.95,
      knowledge_class: 'fact',
      risk_level: 'high',
      reference_date: oferta.data_proposta || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (termosJuridicos.base_area && termosJuridicos.base_area !== 'nao_informado') {
    const desc = termosJuridicos.base_area === 'ad_corpus'
      ? 'ad corpus (área total, sem medição)'
      : termosJuridicos.base_area === 'ad_mensuram'
        ? 'ad mensuram (medição específica)'
        : termosJuridicos.base_area;
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'property_basis',
      content: `Base de área: ${desc}`,
      structured_data: { base_area: termosJuridicos.base_area },
      entity_type: 'imovel',
      entity_id: null,
      confidence: 0.90,
      knowledge_class: 'fact',
      risk_level: 'medium',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (termosJuridicos.corretagem && termosJuridicos.corretagem !== 'nao_informado') {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'negotiation',
      subcategory: 'brokerage',
      content: `Comissão de corretagem: responsabilidade da ${termosJuridicos.corretagem}`,
      structured_data: { corretagem: termosJuridicos.corretagem },
      entity_type: null,
      entity_id: null,
      confidence: 0.95,
      knowledge_class: 'fact',
      risk_level: 'medium',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (termosJuridicos.status_assinatura && termosJuridicos.status_assinatura !== 'nao_informado') {
    const labels = {
      assinada_proponente: 'Assinada pelo proponente, aguardando aceite',
      aceita: 'Aceita pelas partes',
      aceite_em_branco: 'Campo de aceite em branco (não assinada pelo representante do imóvel)',
    };
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'signature_status',
      content: labels[termosJuridicos.status_assinatura] || termosJuridicos.status_assinatura,
      structured_data: { status_assinatura: termosJuridicos.status_assinatura },
      entity_type: null,
      entity_id: null,
      confidence: 0.85,
      knowledge_class: 'fact',
      risk_level: 'high',
      reference_date: oferta.data_proposta || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  // ── Condições / prazos ─────────────────────────────────────
  if (condicoes.due_diligence_dias) {
    let txt = `Due diligence: ${condicoes.due_diligence_dias} dias`;
    if (condicoes.due_diligence_gatilho) txt += ` a partir de ${condicoes.due_diligence_gatilho}`;
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'due_diligence_period',
      content: txt,
      structured_data: {
        due_diligence_dias: condicoes.due_diligence_dias,
        due_diligence_gatilho: condicoes.due_diligence_gatilho || null,
      },
      entity_type: null,
      entity_id: null,
      confidence: 0.92,
      knowledge_class: 'fact',
      risk_level: 'high',
      reference_date: oferta.data_proposta || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (Array.isArray(condicoes.due_diligence_escopo) && condicoes.due_diligence_escopo.length > 0) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'due_diligence_scope',
      content: `Escopo DD: ${condicoes.due_diligence_escopo.join(', ')}`,
      structured_data: { due_diligence_escopo: condicoes.due_diligence_escopo },
      entity_type: null,
      entity_id: null,
      confidence: 0.85,
      knowledge_class: 'fact',
      risk_level: 'medium',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (Array.isArray(condicoes.condicoes_precedentes) && condicoes.condicoes_precedentes.length > 0) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'conditions_precedent',
      content: `Condições precedentes: ${condicoes.condicoes_precedentes.join('; ')}`,
      structured_data: { condicoes_precedentes: condicoes.condicoes_precedentes },
      entity_type: null,
      entity_id: null,
      confidence: 0.88,
      knowledge_class: 'fact',
      risk_level: 'high',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (condicoes.condicao_posse) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'possession_condition',
      content: `Posse: ${condicoes.condicao_posse}`,
      structured_data: { condicao_posse: condicoes.condicao_posse },
      entity_type: null,
      entity_id: null,
      confidence: 0.90,
      knowledge_class: 'fact',
      risk_level: 'high',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (condicoes.condicao_escritura) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'deed_condition',
      content: `Escrituração: ${condicoes.condicao_escritura}`,
      structured_data: { condicao_escritura: condicoes.condicao_escritura },
      entity_type: null,
      entity_id: null,
      confidence: 0.90,
      knowledge_class: 'fact',
      risk_level: 'high',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (oferta.validade_dias) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'proposal_validity',
      content: `Validade da proposta: ${oferta.validade_dias} dias`,
      structured_data: { validade_dias: oferta.validade_dias },
      entity_type: null,
      entity_id: null,
      confidence: 0.95,
      knowledge_class: 'fact',
      risk_level: 'high',
      reference_date: oferta.data_proposta || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (oferta.opcao_compra_dias) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'purchase_option',
      content: `Opção de compra: ${oferta.opcao_compra_dias} dias após aceite`,
      structured_data: { opcao_compra_dias: oferta.opcao_compra_dias },
      entity_type: null,
      entity_id: null,
      confidence: 0.95,
      knowledge_class: 'fact',
      risk_level: 'high',
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  // ── Riscos ─────────────────────────────────────────────────
  if (riscos.risco_ambiental_mencionado) {
    const detalhes = Array.isArray(riscos.detalhes_risco_ambiental) && riscos.detalhes_risco_ambiental.length > 0
      ? riscos.detalhes_risco_ambiental.join(', ')
      : 'mencionado sem detalhes';
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'risk',
      subcategory: 'environmental',
      content: `Risco ambiental: ${detalhes}`,
      structured_data: {
        risco_ambiental_mencionado: true,
        detalhes_risco_ambiental: riscos.detalhes_risco_ambiental || null,
      },
      entity_type: 'imovel',
      entity_id: null,
      confidence: 0.88,
      knowledge_class: 'fact',
      risk_level: 'high',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (Array.isArray(riscos.riscos_tecnicos) && riscos.riscos_tecnicos.length > 0) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'risk',
      subcategory: 'technical',
      content: `Riscos técnicos: ${riscos.riscos_tecnicos.join(', ')}`,
      structured_data: { riscos_tecnicos: riscos.riscos_tecnicos },
      entity_type: 'imovel',
      entity_id: null,
      confidence: 0.85,
      knowledge_class: 'fact',
      risk_level: 'medium',
      review_status: REVIEW_STATUS_ATOM,
    });
  }
  if (condicoes.retificacao_area_necessaria) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'risk',
      subcategory: 'area_rectification',
      content: 'Retificação de área necessária como condição de aquisição',
      structured_data: { retificacao_area_necessaria: true },
      entity_type: 'imovel',
      entity_id: null,
      confidence: 0.90,
      knowledge_class: 'fact',
      risk_level: 'high',
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  // ── Coverage check (sinaliza lacunas no extracted_json) ────
  const coverage = checkProposalCoverage(j);
  if (!coverage.complete || coverage.warnings.length > 0) {
    const partes = [];
    if (coverage.missing.length > 0) partes.push(`Campos obrigatórios não extraídos: ${coverage.missing.join(', ')}`);
    if (coverage.warnings.length > 0) partes.push(`Recomendados ausentes: ${coverage.warnings.join(', ')}`);
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'coverage',
      subcategory: 'extraction_gaps',
      content: `⚠️ ${partes.join(' · ')}`,
      structured_data: coverage,
      entity_type: null,
      entity_id: null,
      confidence: 1.0,
      knowledge_class: 'fact',
      risk_level: coverage.missing.length > 0 ? 'medium' : 'low',
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  return { ops, atoms };
}

async function mapLandTitleToOps(ir) {
  const raw = ir.extracted_json || {};
  const j = raw.fields || raw;
  const imovel = j.imovel || {};
  const proprietarios = Array.isArray(j.proprietarios) ? j.proprietarios : [];
  const averbacoes = Array.isArray(j.averbacoes) ? j.averbacoes : [];
  const onus = Array.isArray(j.onus) ? j.onus : [];

  const ops = [];
  const atoms = [];

  if (!imovel.matricula) {
    return { ops, atoms };
  }

  const imovelResolved = await resolveImovel(imovel.matricula);

  // ── update/create imóvel ───────────────────────────────────
  const imovelData = {
    matricula: formatMatricula(imovel.matricula) || imovel.matricula,
    cri: imovel.cri || null,
    inscricao_municipal: imovel.inscricao_municipal || null,
    endereco: imovel.endereco || null,
    numero: imovel.numero || null,
    bairro: imovel.bairro || null,
    cidade: imovel.cidade || null,
    uf: imovel.uf || null,
    area_terreno_m2: imovel.area_terreno_m2 || null,
    area_construida_m2: imovel.area_construida_m2 || null,
    tipo_uso_atual: imovel.tipo_uso_atual || null,
    status_juridico: imovel.status_juridico || null,
  };

  if (imovelResolved) {
    ops.push({
      intake_run_id: ir.id,
      operation: 'update_entity',
      target_table: 'imoveis',
      target_id: imovelResolved.id,
      proposed_data: imovelData,
      match_strategy: 'matricula',
      match_candidates: [{ id: imovelResolved.id, label: imovelResolved.nome, score: 1.0, matched_field: `matricula ${imovelResolved.matricula}` }],
      resolved_to: imovelResolved.id,
      confidence: 0.95,
      source_document_id: ir.attachment_id || null,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'update_entity', 'imoveis', imovelResolved.id),
      review_status: REVIEW_STATUS_OP,
    });
  } else {
    ops.push({
      intake_run_id: ir.id,
      operation: 'create_entity',
      target_table: 'imoveis',
      proposed_data: { ...imovelData, nome: imovel.nome || `Matrícula ${imovel.matricula}` },
      match_strategy: 'none',
      confidence: 0.80,
      source_document_id: ir.attachment_id || null,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'create_entity', 'imoveis', formatMatricula(imovel.matricula)),
      review_status: REVIEW_STATUS_OP,
    });
  }

  // ── link_document attachment ↔ imóvel ──────────────────────
  if (ir.attachment_id && imovelResolved) {
    ops.push({
      intake_run_id: ir.id,
      operation: 'link_document',
      target_table: 'document_links',
      proposed_data: {
        attachment_id: ir.attachment_id,
        entity_type: 'imovel',
        entity_id: imovelResolved.id,
        relation_type: 'matricula',
      },
      match_strategy: 'exact',
      confidence: 0.95,
      source_document_id: ir.attachment_id,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'link_document', 'document_links', imovelResolved.id),
      review_status: REVIEW_STATUS_OP,
    });
  }

  // ── proprietários → imovel_proprietarios ───────────────────
  for (const p of proprietarios) {
    const isPJ = p.tipo_pessoa === 'pj' || onlyDigits(p.cpf_cnpj).length === 14;
    let proprietarioEmpresaId = null;
    let proprietarioContatoId = null;
    let strategy = 'none';
    let candidates = null;

    if (isPJ) {
      const r = await resolveEmpresa(p.nome, p.cpf_cnpj);
      proprietarioEmpresaId = r.id;
      strategy = r.strategy;
      candidates = r.candidates?.slice(0, 3) || null;
    } else {
      const c = await resolveContatoByNome(p.nome);
      if (c) {
        proprietarioContatoId = c.id;
        strategy = 'fuzzy';
        candidates = [{ id: c.id, label: `${c.first_name} ${c.last_name || ''}`.trim(), score: 0.85, matched_field: 'nome' }];
      }
    }

    const ownerKey = `${imovelResolved?.id || formatMatricula(imovel.matricula)}|${onlyDigits(p.cpf_cnpj) || upper(p.nome)}`;

    ops.push({
      intake_run_id: ir.id,
      operation: 'create_edge',
      target_table: 'imovel_proprietarios',
      proposed_data: {
        imovel_id: imovelResolved?.id || null,
        proprietario_empresa_id: proprietarioEmpresaId,
        proprietario_contato_id: proprietarioContatoId,
        percentual_participacao: p.percentual ?? null,
        tipo_propriedade: p.tipo_propriedade || null,
        data_aquisicao: p.data_aquisicao || null,
        proprietario_nome_extraido: p.nome,
        proprietario_cpf_cnpj_extraido: p.cpf_cnpj,
      },
      match_strategy: strategy,
      match_candidates: candidates,
      confidence: proprietarioEmpresaId || proprietarioContatoId ? 0.88 : 0.65,
      source_document_id: ir.attachment_id || null,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'create_edge', 'imovel_proprietarios', ownerKey),
      review_status: REVIEW_STATUS_OP,
    });

    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'ownership',
      subcategory: isPJ ? 'pj_owner' : 'pf_owner',
      content: `Proprietário${isPJ ? '' : 'a/o'}: ${p.nome}${p.percentual != null ? ` (${p.percentual}%)` : ''}${p.cpf_cnpj ? ` · ${p.cpf_cnpj}` : ''}`,
      structured_data: { ...p },
      entity_type: 'imovel',
      entity_id: imovelResolved?.id || null,
      confidence: 0.92,
      knowledge_class: 'fact',
      risk_level: 'high',
      reference_date: p.data_aquisicao || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  // ── Atoms: geographic, legal ───────────────────────────────
  if (imovel.cri) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'geographic',
      subcategory: 'registry',
      content: `Cartório: ${imovel.cri}`,
      structured_data: { cri: imovel.cri },
      entity_type: 'imovel',
      entity_id: imovelResolved?.id || null,
      confidence: 0.98,
      knowledge_class: 'fact',
      risk_level: 'low',
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  if (imovel.endereco) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'geographic',
      subcategory: 'address',
      content: `Endereço: ${[imovel.endereco, imovel.numero, imovel.bairro, imovel.cidade, imovel.uf].filter(Boolean).join(', ')}`,
      structured_data: {
        endereco: imovel.endereco, numero: imovel.numero, bairro: imovel.bairro,
        cidade: imovel.cidade, uf: imovel.uf,
      },
      entity_type: 'imovel',
      entity_id: imovelResolved?.id || null,
      confidence: 0.96,
      knowledge_class: 'fact',
      risk_level: 'low',
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  for (const av of averbacoes) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'averbacao',
      content: `${av.numero || 'Averbação'} (${av.data || 's/d'}): ${(av.descricao || '').slice(0, 240)}`,
      structured_data: { ...av },
      entity_type: 'imovel',
      entity_id: imovelResolved?.id || null,
      confidence: 0.93,
      knowledge_class: 'fact',
      risk_level: 'high',
      reference_date: av.data || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  for (const o of onus) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'legal',
      subcategory: 'onus',
      content: `Ônus${o.cancelado ? ' (CANCELADO)' : ''}: ${o.tipo || ''}${o.valor ? ` R$ ${o.valor.toLocaleString('pt-BR')}` : ''}${o.credor_nome ? ` · ${o.credor_nome}` : ''}`,
      structured_data: { ...o },
      entity_type: 'imovel',
      entity_id: imovelResolved?.id || null,
      confidence: 0.93,
      knowledge_class: 'fact',
      risk_level: 'high',
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  return { ops, atoms };
}

async function mapCorporateCharterToOps(ir) {
  const raw = ir.extracted_json || {};
  const j = raw.fields || raw;
  const empresa = j.empresa || {};
  const socios = Array.isArray(j.socios) ? j.socios : [];

  const ops = [];
  const atoms = [];

  if (!empresa.razao_social && !empresa.cnpj) {
    return { ops, atoms };
  }

  const empresaResolved = await resolveEmpresa(empresa.razao_social, empresa.cnpj);

  // ── update/create empresa ──────────────────────────────────
  const empresaData = {
    company_name: upper(empresa.razao_social) || null,
    cnpj: empresa.cnpj || null,
    objeto_social_extraido: empresa.objeto_social || null,
    capital_social_extraido: empresa.capital_social || null,
    natureza_juridica: empresa.natureza_juridica || null,
    endereco_extraido: empresa.endereco || null,
    cidade_extraida: empresa.cidade || null,
    uf_extraida: empresa.uf || null,
    data_constituicao: empresa.data_constituicao || null,
  };

  if (empresaResolved.id) {
    ops.push({
      intake_run_id: ir.id,
      operation: 'update_entity',
      target_table: 'empresas',
      target_id: empresaResolved.id,
      proposed_data: empresaData,
      match_strategy: empresaResolved.strategy,
      match_candidates: empresaResolved.candidates?.slice(0, 3) || null,
      resolved_to: empresaResolved.id,
      confidence: 0.92,
      source_document_id: ir.attachment_id || null,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'update_entity', 'empresas', empresaResolved.id),
      review_status: REVIEW_STATUS_OP,
    });
  } else {
    ops.push({
      intake_run_id: ir.id,
      operation: 'create_entity',
      target_table: 'empresas',
      proposed_data: empresaData,
      match_strategy: 'none',
      confidence: 0.75,
      source_document_id: ir.attachment_id || null,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'create_entity', 'empresas', onlyDigits(empresa.cnpj) || upper(empresa.razao_social)),
      review_status: REVIEW_STATUS_OP,
    });
  }

  // ── link_document attachment ↔ empresa ─────────────────────
  if (ir.attachment_id && empresaResolved.id) {
    ops.push({
      intake_run_id: ir.id,
      operation: 'link_document',
      target_table: 'document_links',
      proposed_data: {
        attachment_id: ir.attachment_id,
        entity_type: 'empresa',
        entity_id: empresaResolved.id,
        relation_type: 'jucesp_filing',
      },
      match_strategy: 'exact',
      confidence: 0.92,
      source_document_id: ir.attachment_id,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'link_document', 'document_links', empresaResolved.id),
      review_status: REVIEW_STATUS_OP,
    });
  }

  // ── sócios → socios ────────────────────────────────────────
  for (const s of socios) {
    const isPJ = s.tipo_pessoa === 'pj' || onlyDigits(s.cpf_cnpj).length === 14;
    let socioContatoId = null;
    let socioEmpresaId = null;
    let strategy = 'none';
    let candidates = null;

    if (isPJ) {
      const r = await resolveEmpresa(s.nome, s.cpf_cnpj);
      socioEmpresaId = r.id;
      strategy = r.strategy;
      candidates = r.candidates?.slice(0, 3) || null;
    } else {
      const c = await resolveContatoByNome(s.nome);
      if (c) {
        socioContatoId = c.id;
        strategy = 'fuzzy';
        candidates = [{ id: c.id, label: `${c.first_name} ${c.last_name || ''}`.trim(), score: 0.85, matched_field: 'nome' }];
      }
    }

    const isAdmin = s.tipo_socio === 'managing_partner'
      || /administrador/i.test(s.qualificacao || '');

    const socioKey = `${empresaResolved.id || onlyDigits(empresa.cnpj)}|${onlyDigits(s.cpf_cnpj) || upper(s.nome)}`;

    ops.push({
      intake_run_id: ir.id,
      operation: 'create_edge',
      target_table: 'socios',
      proposed_data: {
        empresa_id: empresaResolved.id || null,
        socio_contato_id: socioContatoId,
        socio_empresa_id: socioEmpresaId,
        percentual_participacao: s.percentual ?? null,
        tipo_socio: s.tipo_socio || (isAdmin ? 'managing_partner' : 'partner'),
        is_active: true,
        socio_nome_extraido: s.nome,
        socio_cpf_cnpj_extraido: s.cpf_cnpj,
        qualificacao: s.qualificacao || null,
      },
      match_strategy: strategy,
      match_candidates: candidates,
      confidence: socioContatoId || socioEmpresaId ? 0.88 : 0.70,
      source_document_id: ir.attachment_id || null,
      idempotency_key: makeIdempotencyKey(ir.attachment_id, 'create_edge', 'socios', socioKey),
      review_status: REVIEW_STATUS_OP,
    });

    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'corporate',
      subcategory: isAdmin ? 'managing_partner' : 'partner',
      content: `${isAdmin ? 'Sócio administrador' : 'Sócio'}: ${s.nome}${s.percentual != null ? ` (${s.percentual}%)` : ''}${s.qualificacao ? ` — ${s.qualificacao}` : ''}`,
      structured_data: { ...s },
      entity_type: 'empresa',
      entity_id: empresaResolved.id || null,
      confidence: 0.93,
      knowledge_class: 'fact',
      risk_level: isAdmin ? 'high' : 'medium',
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  // ── Atoms: capital_social, objeto_social ───────────────────
  if (empresa.capital_social) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'corporate',
      subcategory: 'capital_social',
      content: `Capital social: R$ ${Number(empresa.capital_social).toLocaleString('pt-BR')}`,
      structured_data: { capital_social: empresa.capital_social },
      entity_type: 'empresa',
      entity_id: empresaResolved.id || null,
      confidence: 0.95,
      knowledge_class: 'fact',
      risk_level: 'medium',
      reference_date: empresa.data_constituicao || null,
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  if (empresa.objeto_social) {
    atoms.push({
      intake_run_id: ir.id,
      atom_type: 'fact',
      category: 'corporate',
      subcategory: 'objeto_social',
      content: `Objeto social: ${empresa.objeto_social.slice(0, 240)}`,
      structured_data: { objeto_social: empresa.objeto_social },
      entity_type: 'empresa',
      entity_id: empresaResolved.id || null,
      confidence: 0.94,
      knowledge_class: 'fact',
      risk_level: 'low',
      review_status: REVIEW_STATUS_ATOM,
    });
  }

  return { ops, atoms };
}

/* ── Dispatcher principal ───────────────────────────────────────────────── */

export async function proposeFromExtraction(intakeRun) {
  const result = { opsCreated: 0, atomsCreated: 0, atomsAutoApproved: 0, skipped: 0, errors: [] };

  const ej = intakeRun?.extracted_json;
  if (!ej || typeof ej !== 'object') return { ...result, skipped: 1 };
  // Schema v2 envolve em "fields"; v1 é plano. Aceita ambos no gate.
  const ejInner = ej.fields && typeof ej.fields === 'object' ? ej.fields : ej;
  if (Object.keys(ejInner).length === 0) return { ...result, skipped: 1 };

  const docType = normalizeDocumentType(intakeRun.document_type);

  try {
    let ops = [];
    let atoms = [];

    switch (docType) {
      case 'proposal':
        ({ ops, atoms } = await mapProposalToOps(intakeRun));
        break;
      case 'land_title':
        ({ ops, atoms } = await mapLandTitleToOps(intakeRun));
        break;
      case 'corporate_charter':
        ({ ops, atoms } = await mapCorporateCharterToOps(intakeRun));
        break;
      default:
        return { ...result, skipped: 1 };
    }

    if (ops.length === 0 && atoms.length === 0) {
      return { ...result, skipped: 1 };
    }

    const opsRes = await insertOps(ops);
    const atomsRes = await insertAtoms(atoms, intakeRun.id);

    // Auto-approve policy: atoms 'document'+confidence≥0.85 viram knowledge_atoms
    // imediatamente. Os que ficam abaixo seguem na fila para revisão humana.
    const autoRes = atomsRes.created > 0
      ? await autoApproveEligibleAtoms(intakeRun.id)
      : { autoApproved: 0, errors: [] };

    // Atualiza pipeline_status para 'proposed' (mantém invariante de "tem staging").
    if (opsRes.created > 0 || atomsRes.created > 0) {
      const { error: updErr } = await supabase
        .from('intake_runs')
        .update({ pipeline_status: 'proposed', updated_at: new Date().toISOString() })
        .eq('id', intakeRun.id);
      if (updErr) {
        console.warn('[proposeFromExtraction] update pipeline_status falhou:', updErr.message);
      }
    }

    return {
      opsCreated: opsRes.created,
      atomsCreated: atomsRes.created,
      atomsAutoApproved: autoRes.autoApproved,
      skipped: opsRes.skipped + atomsRes.skipped,
      errors: [...opsRes.errors, ...atomsRes.errors, ...autoRes.errors],
    };
  } catch (err) {
    console.error('[proposeFromExtraction] dispatcher falhou:', err);
    return { ...result, errors: [err.message || String(err)] };
  }
}
